import React, { useRef, useEffect, useState } from "react";
import { Marker, Circle, Tooltip } from "react-leaflet";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { IGateway } from "../../types";
import { updateGatewayPosition } from "../../store/gatewaysSlice";
import { ApiService } from "../../services/api";
import { selectCurrentMap } from "../../store/mapsSlice";
import { convertRelationalToPixelRadius } from "../../utils/radiusUtils";
import L from "leaflet";
import Icon from "@mdi/react";
import { mdiRouterWireless } from "@mdi/js";

interface GatewayMarkerProps {
  gateway: IGateway;
  showCoverage?: boolean;
}

// Create custom gateway icon with MDI router-wireless icon
const createGatewayIcon = (isSelected: boolean) => {
  const size = isSelected ? 51 : 45;
  const iconSize = size - 8; // Account for border
  const iconColor = "#7a00cc";

  return L.divIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        padding: 4px;
        border: ${isSelected ? "4px solid #ffffff" : "none"};
        display: flex;
        align-items: center;
        justify-content: center;
        filter: drop-shadow(0 2px 6px rgba(0,0,0,0.4));
        ${isSelected ? "animation: pulse 2s infinite;" : ""}
      ">
        <svg width="${iconSize}" height="${iconSize}" viewBox="0 0 24 24" fill="${iconColor}">
          <path d="M20.2,5.9L21,5.1C19.6,3.7 17.8,3 16,3C14.2,3 12.4,3.7 11,5.1L11.8,5.9C13,4.8 14.5,4.2 16,4.2C17.5,4.2 19,4.8 20.2,5.9M19.3,6.7C18.4,5.8 17.2,5.3 16,5.3C14.8,5.3 13.6,5.8 12.7,6.7L13.5,7.5C14.2,6.8 15.1,6.5 16,6.5C16.9,6.5 17.8,6.8 18.5,7.5L19.3,6.7M19,13H17V9H15V13H5A2,2 0 0,0 3,15V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V15A2,2 0 0,0 19,13M8,18H6V16H8V18M11.5,18H9.5V16H11.5V18M15,18H13V16H15V18Z"/>
        </svg>
      </div>
      ${
        isSelected
          ? `
        <style>
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }
        </style>
      `
          : ""
      }
    `,
    className: "gateway-marker",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const GatewayMarker: React.FC<GatewayMarkerProps> = ({
  gateway,
  showCoverage = true,
}) => {
  const dispatch = useDispatch();
  const { language, isEditMode, selectedGatewayMac } = useSelector(
    (state: RootState) => state.ui
  );
  const { entities: gateways } = useSelector(
    (state: RootState) => state.gateways
  );
  const { entities: devices } = useSelector(
    (state: RootState) => state.devices
  );
  const currentMap = useSelector(selectCurrentMap);
  const markerRef = useRef<L.Marker>(null);
  const [forceRender, setForceRender] = useState(0);
  const iconRef = useRef<HTMLDivElement>(null);

  // Force re-render when gateway properties change
  useEffect(() => {
    // console.log(
    //   `🔄 GatewayMarker ${gateway.mac} props changed, forcing re-render`
    // );
    setForceRender((prev) => prev + 1);
  }, [gateway.viewRadius, gateway.radius, gateway.logicThreshold, gateway.mac]);

  // Validate gateway position
  if (
    !gateway.position ||
    typeof gateway.position.x !== "number" ||
    typeof gateway.position.y !== "number" ||
    isNaN(gateway.position.x) ||
    isNaN(gateway.position.y)
  ) {
    console.warn("Invalid gateway position:", gateway);
    return null;
  }

  // Use pixel coordinates directly with L.CRS.Simple
  // In L.CRS.Simple, coordinates are [y, x] (lat, lng equivalent)
  const gatewayLatLng: [number, number] = [
    gateway.position.y,
    gateway.position.x,
  ];

  // Check if this gateway is selected
  const isSelected = selectedGatewayMac === gateway.mac;

  // Get map dimensions for relational radius calculation
  const mapDimensions = currentMap?.settings?.imageSize
    ? {
        width: currentMap.settings.imageSize.width,
        height: currentMap.settings.imageSize.height,
      }
    : { width: 800, height: 600 }; // Default fallback

  // Convert relational radius (0-100) to pixel radius
  const relationalRadius = gateway.viewRadius ?? gateway.radius ?? 25; // Default to 25% if undefined
  const pixelRadius = convertRelationalToPixelRadius(
    relationalRadius,
    mapDimensions
  );

  // console.log(`🗺 GatewayMarker ${gateway.mac} rendering:`, {
  //   relationalRadius,
  //   pixelRadius,
  //   mapDimensions,
  //   viewRadius: gateway.viewRadius,
  //   radius: gateway.radius,
  // });

  // Handle drag end event
  const handleDragEnd = () => {
    const marker = markerRef.current;
    if (marker && isEditMode) {
      const newPosition = marker.getLatLng();
      // Convert back from lat/lng to x/y coordinates
      const newX = newPosition.lng;
      const newY = newPosition.lat;

      // Update gateway position in Redux store
      dispatch(
        updateGatewayPosition({
          mac: gateway.mac,
          position: { x: newX, y: newY },
        })
      );

      // NOTE: Device position recalculation is now handled by the backend
      // The frontend no longer performs triangulation calculations
      // Device positions will be updated via SSE from the backend service

      // console.log(
      //   `🔄 Gateway ${gateway.mac} moved to (${newX.toFixed(1)}, ${newY.toFixed(
      //     1
      //   )}), device positions will be recalculated by backend`
      // );
    }
  };

  return (
    <>
      {/* Gateway coverage radius */}
      {showCoverage && (
        <Circle
          key={`${gateway.mac}-circle-${relationalRadius}-${pixelRadius}-${forceRender}`}
          center={gatewayLatLng}
          radius={pixelRadius}
          pathOptions={{
            color: isSelected ? "#3b82f6" : "#7a00cc",
            fillColor: isSelected ? "#3b82f6" : "#7a00cc",
            fillOpacity: isSelected ? 0.15 : 0.1,
            weight: isSelected ? 3 : 2,
            opacity: isSelected ? 0.8 : 0.6,
          }}
        />
      )}

      {/* Gateway marker */}
      <Marker
        position={gatewayLatLng}
        icon={createGatewayIcon(isSelected)}
        draggable={isEditMode}
        ref={markerRef}
        eventHandlers={{
          dragend: handleDragEnd,
        }}
      >
        <Tooltip
          direction="bottom"
          offset={[0, 15]}
          className="gateway-tooltip"
        >
          <div className="text-sm">
            <div className="font-semibold text-primary-600">{gateway.name}</div>
            <div className="text-gray-600 text-xs">MAC: {gateway.mac}</div>
            <div className="text-gray-500 text-xs">
              {language === "he" ? "רדיוס" : "Radius"}: {relationalRadius}%
              <span className="text-gray-400">({pixelRadius}px)</span>
            </div>
          </div>
        </Tooltip>
      </Marker>
    </>
  );
};

export default GatewayMarker;
